import * as Sentry from '@sentry/browser';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster, ToastReadySignal, toast } from '@/ui/components/toast';
import { ToastMessage } from '@/ui/components/toastMessage';
import { createToastQueue } from './toastQueue';
import type { ToastInput } from './toastTypes';

const HOST_ID = 'toast-host';

const queue = createToastQueue<ToastInput>(input => {
  toast.add({
    title: input.title || undefined,
    description: React.createElement(ToastMessage, { html: input.message }),
    type: input.type,
    // danger は alertdialog として即時に読み上げる
    priority: input.type === 'danger' ? 'high' : 'low',
  });
});

export function showToast(input: ToastInput) {
  queue.enqueue(input);
}

// サーバが描画時に埋めた flash。mountToaster が読み、onToasterReady が流す
let initialFlash: ToastInput[] = [];

// ToastReadySignal は onReady の identity が変わるたびに再び呼ぶため、モジュール直下の安定した関数にする
const onToasterReady = () => {
  queue.ready();
  initialFlash.splice(0).forEach(showToast);
};

// レイアウトが出力する #toast-host に Toaster を 1 回だけ描画する。
// 両 pack から呼ぶが、同じ document で 2 回目以降は何もしない。
// 戻り値の関数は Toaster を外す（story 間で Toaster を持ち越さないため、Storybook だけが使う）
export function mountToaster() {
  const host = document.getElementById(HOST_ID);
  if (!host || host.dataset.mounted === 'true') return undefined;

  initialFlash = readFlash(host);
  host.dataset.mounted = 'true';
  // 前の Toaster を外したあとの再描画（Storybook）でも、ToastReadySignal まで貯め直す
  queue.reset();
  const root = createRoot(host);
  root.render(
    React.createElement(
      Toaster,
      null,
      React.createElement(ToastReadySignal, { onReady: onToasterReady }),
    ),
  );
  return () => {
    root.unmount();
    delete host.dataset.mounted;
  };
}

// growl_flash が data-flash に入れた [{ type, message }] を 1 回だけ読み、属性を消す
// （bfcache から戻ったときに再生しないため）
function readFlash(host: HTMLElement): ToastInput[] {
  const raw = host.dataset.flash;
  delete host.dataset.flash;
  if (!raw) return [];
  // 壊れた flash だけを捨てる。投げると Toaster が立たず、そのページの notify もすべて出なくなる
  try {
    const entries = JSON.parse(raw) as {
      type: ToastInput['type'];
      message: string;
    }[];
    return entries.map(e => ({ title: '', message: e.message, type: e.type }));
  } catch (error) {
    Sentry.captureException(error);
    return [];
  }
}

export function mountToasterOnLoad() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountToaster, { once: true });
  } else {
    mountToaster();
  }
}
